import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  Layers,
  Settings,
  Store,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../UI/Popover";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../UI/Avatar";

/**
 * ShopSwitcher - Dropdown "Chọn Zalo" nằm đầu TopNavBar.
 * Hỗ trợ 3 mode:
 *  - Single shop (tick 1 + Áp dụng) → /chat/:shopId
 *  - Ad-hoc multi (tick ≥2 + Áp dụng) → /chat/multi/:shopIds
 *  - Saved shop group (click group) → /chat/group/:groupId
 */
export default function ShopSwitcher({
  shops = [],
  shopGroups = [],
  activeGroup,
  currentUser,
  selectedShopIds = [],
  changeShop,
  onApplyMultiSelect,
  onChangeGroup,
  singleSelect = false,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => new Set(selectedShopIds));
  const statusByShopId = useSelector((state) => state.zaloStatus.byShopId);

  useEffect(() => {
    if (!open) setDraft(new Set(selectedShopIds));
  }, [open, selectedShopIds]);

  const activeAccount = activeGroup
    ? { name: activeGroup.name, avatar: activeGroup.avatar }
    : selectedShopIds.length > 1
      ? {
          name: `${selectedShopIds.length} tài khoản`,
          avatar: null,
        }
      : currentUser;

  const isSameAsCurrent = useMemo(() => {
    if (draft.size !== selectedShopIds.length) return false;
    for (const id of selectedShopIds) {
      if (!draft.has(id)) return false;
    }
    return true;
  }, [draft, selectedShopIds]);

  const toggleDraft = (id, checked) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleApply = () => {
    const ids = [...draft];
    if (ids.length === 0) return;
    setOpen(false);
    if (ids.length === 1) {
      const shop = shops.find((s) => s._id === ids[0]);
      if (shop) changeShop?.(shop);
    } else {
      onApplyMultiSelect?.(ids);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border bg-white text-gray-700 border-gray-200 hover:bg-gray-50 text-[12px] font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
          title="Chọn tài khoản Zalo"
          aria-label="Chọn tài khoản Zalo"
        >
          {activeAccount?.avatar ? (
            <Avatar className="h-4 w-4">
              <AvatarImage src={activeAccount.avatar} alt={activeAccount.name} />
              <AvatarFallback className="text-[9px]">
                {activeAccount.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
          ) : (
            <Store size={14} />
          )}
          <span className="truncate max-w-[140px]">
            {activeAccount?.name || "Chọn Zalo"}
          </span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0 rounded-xl shadow-lg border border-gray-100"
        side="bottom"
        align="start"
        sideOffset={6}
      >
        <div className="py-1">
          <div className="px-2.5 py-1.5 text-[12px] font-medium text-gray-800 border-b border-gray-100 truncate">
            {activeAccount?.name || "Chọn tài khoản"}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {shops.length > 0 && (
              <>
                <div className="px-2.5 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  Tài khoản
                </div>
                {shops.map((shop) => {
                  const checked = draft.has(shop._id);
                  const online = Boolean(statusByShopId[shop._id]?.isLogin);
                  // Mode single-select: render button, click → chọn ngay
                  if (singleSelect) {
                    return (
                      <button
                        key={shop._id}
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          changeShop?.(shop);
                        }}
                        className="w-full flex items-center px-2.5 py-1.5 text-[12px] hover:bg-gray-50 transition-colors cursor-pointer gap-2 text-left"
                      >
                        <div className="flex items-center flex-1 min-w-0 gap-2">
                          <div className="relative shrink-0">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={shop.avatar} alt={shop.name} />
                              <AvatarFallback className="text-[10px]">
                                {shop.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${
                                online ? "bg-green-500" : "bg-gray-300"
                              }`}
                              aria-hidden="true"
                              title={
                                online ? "Đang đăng nhập" : "Chưa đăng nhập"
                              }
                            />
                          </div>
                          <span className="truncate">{shop.name}</span>
                        </div>
                        {checked && (
                          <Check
                            size={13}
                            className="text-indigo-500 flex-shrink-0"
                          />
                        )}
                      </button>
                    );
                  }
                  // Mode multi-select (default): checkbox + Áp dụng
                  return (
                    <label
                      key={shop._id}
                      className="w-full flex items-center px-2.5 py-1.5 text-[12px] hover:bg-gray-50 transition-colors cursor-pointer gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleDraft(shop._id, e.target.checked)}
                        className="h-3.5 w-3.5 accent-indigo-500 shrink-0 cursor-pointer"
                      />
                      <div className="flex items-center flex-1 min-w-0 gap-2">
                        <div className="relative shrink-0">
                          <Avatar className="h-5 w-5">
                            <AvatarImage src={shop.avatar} alt={shop.name} />
                            <AvatarFallback className="text-[10px]">
                              {shop.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${
                              online ? "bg-green-500" : "bg-gray-300"
                            }`}
                            aria-hidden="true"
                            title={online ? "Đang đăng nhập" : "Chưa đăng nhập"}
                          />
                        </div>
                        <span className="truncate">{shop.name}</span>
                      </div>
                    </label>
                  );
                })}
              </>
            )}

            {shopGroups.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-1"></div>
                <div className="px-2.5 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  Nhóm tài khoản
                </div>
                {shopGroups.map((group) => (
                  <button
                    key={group._id}
                    className="w-full flex items-center px-2.5 py-1.5 text-[12px] hover:bg-gray-50 transition-colors text-left"
                    onClick={() => {
                      onChangeGroup?.(group);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center flex-1 min-w-0 gap-2">
                      <div className="h-5 w-5 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center overflow-hidden shrink-0">
                        {group.avatar ? (
                          <img
                            src={group.avatar}
                            alt={group.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Layers size={11} className="text-white" />
                        )}
                      </div>
                      <span className="truncate">{group.name}</span>
                      <span className="ml-1 text-[10px] text-gray-400 shrink-0">
                        ({group.shopIds?.length || 0})
                      </span>
                    </div>
                    {activeGroup?._id === group._id && (
                      <Check
                        size={13}
                        className="text-green-500 flex-shrink-0 ml-2"
                      />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
          {!singleSelect && (
            <div className="border-t border-gray-100 mt-1 px-2.5 py-1.5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-gray-500">
                Đã chọn {draft.size}
              </span>
              <button
                type="button"
                disabled={draft.size === 0 || isSameAsCurrent}
                onClick={handleApply}
                className="text-[12px] px-2.5 py-1 rounded-md bg-indigo-500 text-white hover:bg-indigo-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Áp dụng
              </button>
            </div>
          )}
          <div className="border-t border-gray-100 pt-1">
            <Link
              to="/shop"
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] hover:bg-gray-50 transition-colors text-left"
              onClick={() => setOpen(false)}
            >
              <Settings size={13} className="text-gray-500" />
              <span>Quản lý Zalo</span>
            </Link>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
